// Sweden expansion -- see doc/sweden-expansion-currency-plan.md. Resolves a tenantCompanyClusterId
// (the FK used on customer/portfolio/contract/order/invoice rows, see
// doc/db-schema-and-immutable-pattern.md) to that tenant's currency code, using the tenant list
// already loaded via tenantdata.js (api/tenant/list).
import { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import store from '../store';
import { getTenants } from '../modules/tenantdata';
import { getExchangeRate, rateKey } from '../modules/currencyratedata';

// Falls back to 'EUR' when unresolved -- every pre-Sweden-expansion row has no tenant reference (or
// belongs to the FI tenant), so EUR is always a safe default, never a silent wrong guess.
export const resolveCurrencyForTenantClusterId = (tenants, tenantCompanyClusterId) => {
    if (!tenantCompanyClusterId || !tenants || tenants.length === 0) return 'EUR';
    const tenant = tenants.find((t) => t.clusterId === tenantCompanyClusterId);
    return tenant ? tenant.currency : 'EUR';
};

// Same resolution, but reads directly from the redux store -- for class components that don't use
// connect()/hooks (see locationeditview.jsx's existing `store.getState()`/`store.dispatch` pattern).
export const resolveCurrencyForTenantClusterIdFromStore = (tenantCompanyClusterId) => {
    const state = store.getState();
    const tenantdata = state.tenantdata || {};
    return resolveCurrencyForTenantClusterId(tenantdata.tenants, tenantCompanyClusterId);
};

// Currency of the user's currently active/selected tenant, read directly from the redux store -- for
// class components that don't use hooks (see useActiveTenantCurrency below for the hook equivalent).
export const resolveActiveTenantCurrencyFromStore = () => {
    const state = store.getState();
    const tenantdata = state.tenantdata || {};
    const tenants = tenantdata.tenants || [];
    const tenant = tenants.find((t) => t.id === tenantdata.activeTenantId);
    return tenant ? tenant.currency : 'EUR';
};

// Ensures the current user's assigned tenants (tenantdata.js) are loaded, fetching them once if not
// already present, and returns the list for currency (or other tenant-field) lookups. Skips the
// fetch when the tenantdata slice itself is absent from the store (test fixtures with a partial
// mock store, not the real app, which always wires tenantdata via combineReducers).
export const useKnownTenants = () => {
    const dispatch = useDispatch();
    const hasTenantDataSlice = useSelector((state) => !!state.tenantdata);
    const tenants = useSelector((state) => (state.tenantdata || {}).tenants || []);
    const isFetching = useSelector((state) => (state.tenantdata || {}).isFetching);

    useEffect(() => {
        if (hasTenantDataSlice && tenants.length === 0 && !isFetching) {
            getTenants()(dispatch);
        }
    }, [hasTenantDataSlice, tenants.length, isFetching, dispatch]);

    return tenants;
};

// Currency of the tenantCompanyClusterId a given entity (customer/portfolio/contract/...) belongs to.
export const useTenantCurrency = (tenantCompanyClusterId) => {
    const tenants = useKnownTenants();
    return resolveCurrencyForTenantClusterId(tenants, tenantCompanyClusterId);
};

// Currency of the user's currently active/selected tenant (see main/tenantswitcher.jsx) -- for
// aggregates implicitly scoped to the active tenant context (dashboard/action views, which per
// doc/sweden-expansion-currency-plan.md are always within a single portfolio/tenant).
export const useActiveTenantCurrency = () => {
    const tenants = useKnownTenants();
    const activeTenantId = useSelector((state) => (state.tenantdata || {}).activeTenantId);
    const tenant = tenants.find((t) => t.id === activeTenantId);
    return tenant ? tenant.currency : 'EUR';
};

// Latest known fromCurrency -> toCurrency daily rate, read directly from the redux store -- for
// class components that don't use hooks (see useExchangeRate below for the hook equivalent, and
// resolveCurrencyForTenantClusterIdFromStore above for the same pattern applied to tenant currency).
// Returns 1 (no conversion) if the pair is identity or no rate has been fetched/cached yet -- callers
// must not treat a 1 fallback as "confirmed 1:1", only as "not yet converted".
export const resolveExchangeRateFromStore = (toCurrency, fromCurrency = 'EUR') => {
    if (!toCurrency || toCurrency === fromCurrency) return 1;
    const state = store.getState();
    const ratesByPair = (state.currencyratedata || {}).ratesByPair || {};
    const entry = ratesByPair[rateKey(fromCurrency, toCurrency)];
    return entry && entry.rate ? entry.rate : 1;
};

// Ensures the current day's fromCurrency -> toCurrency rate is fetched/cached, and returns it (1 if
// not yet loaded or the pair is identity). For function components -- class components should
// dispatch getExchangeRate() directly from their container (see locationeditviewcontainer.jsx).
export const useExchangeRate = (toCurrency, fromCurrency = 'EUR') => {
    const dispatch = useDispatch();
    const rate = useSelector(() => resolveExchangeRateFromStore(toCurrency, fromCurrency));

    useEffect(() => {
        if (toCurrency && toCurrency !== fromCurrency) {
            dispatch(getExchangeRate(fromCurrency, toCurrency));
        }
    }, [toCurrency, fromCurrency, dispatch]);

    return rate;
};
